import React, { useCallback, useEffect, useRef, useState } from "react";
import { FlexBox } from "components/flex-box";
import { H1, H5 } from "components/Typography";
import { AiBuilderSearchBar } from "./AiBuilderSearchBar";
import { Chip, Typography } from "@mui/material";
import { formatCurrency } from "common/utils/string";
import { useRouter } from "next/router";
import useResolution from "hooks/useResolution";
import { set } from "nprogress";

type AIBannerProps = {
  title?: string;
  cover?: string;
  hasSearch?: boolean;
  getAiBuildFunc?: any;
  searchValue: string;
  setSearchValue: (value: string) => void;
  budgetValue: string;
  setBudgetValue: (value: string) => void;
};

const PricesArr = ["10000", "20000", "30000", "50000", "80000"];

const AIBanner: React.FC<AIBannerProps> = ({
  title,
  cover,
  hasSearch,
  getAiBuildFunc,
  searchValue,
  setSearchValue,
  budgetValue,
  setBudgetValue,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);
  const { isXs } = useResolution();
  const [isEnteringBudget, setIsEnteringBudget] = useState(false);

  const handleClick = useCallback(
    (search: string, budget: string) => {
      const finalSearch = search || searchValue;
      const finalBudget = budget || budgetValue;

      if (!finalSearch && !finalBudget) {
        router.push("/ai-builder");
        return;
      }

      setIsLoading(true);

      setSearchValue(finalSearch);
      setBudgetValue(finalBudget);

      getAiBuildFunc(finalSearch, finalBudget);

      const query = {
        ...(finalSearch && { keyword: finalSearch }),
        ...(finalBudget && { budget: finalBudget }),
      };

      if (
        router.query.keyword !== query.keyword ||
        router.query.budget !== query.budget
      ) {
        router.push(
          {
            pathname: "/ai-builder/search/[id]",
            query,
          },
          `/ai-builder/search/${finalSearch || ""}${
            finalBudget ? `&budget=${finalBudget}` : ""
          }`
        );
      }

      setIsLoading(false);
    },
    [
      searchValue,
      budgetValue,
      setSearchValue,
      setBudgetValue,
      getAiBuildFunc,
      router,
    ]
  );

  const handleBudgetValue = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setBudgetValue(value);
    },
    [setBudgetValue]
  );

  const onEnterSearch = useCallback(() => {
    handleClick(searchValue, budgetValue);
  }, [handleClick, searchValue, budgetValue]);

  const handleChipClick = useCallback(
    (value: string) => {
      setBudgetValue(value);
      handleClick(searchValue, value);
    },
    [searchValue, handleClick, setBudgetValue]
  );

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsEnteringBudget(false);
      }
    }

    if (isEnteringBudget) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isEnteringBudget]);

  const ChipList = PricesArr.map((current, index) => (
    <Chip
      key={index}
      disabled={isLoading}
      label={formatCurrency(current, "₱", 0)}
      size="medium"
      clickable
      sx={{
        backgroundColor: "white",
        border: "1px solid rgba(0,0,0,0.5)",
        fontSize: { xs: ".9em", md: "1em" },
        padding: { md: "5px" },
        pointerEvents: !isLoading ? "auto" : "none",
      }}
      onClick={() => handleChipClick(current)}
    />
  ));

  return (
    <FlexBox>
      <div
        style={{
          backgroundImage: `url('${cover}')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          width: "100%",
          height: "40vh",
          maxHeight: "40vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <H1
          style={{
            textAlign: "center",
            fontSize: "3em",
            lineHeight: 1,
            color: "white",
          }}
          maxWidth={600}
        >
          {title}
        </H1>
        <H5
          style={{
            textAlign: "center",
            margin: "0 20px",
            color: "white",
          }}
        >
          {"No experience sa PC Builds? Try mo na to Bossing!"}
        </H5>
        <div style={{ width: "90%", maxWidth: "500px", marginTop: "15px" }}>
          {hasSearch ? (
            <AiBuilderSearchBar
              disabled={isLoading}
              onChangeKeyword={handleClick}
              value={searchValue}
              setSearchValue={setSearchValue}
              budgetValue={budgetValue}
            />
          ) : null}
        </div>
        <Typography
          sx={{
            mt: { xs: "0px", md: "20px" },
            mb: { xs: "0px", md: "5px" },
            fontWeight: "600",
            color: "white",
          }}
        >
          Suggested Budget
        </Typography>
        <FlexBox
          p={1}
          sx={{
            gap: {
              xs: "5px",
              md: "6px",
            },
            flexWrap: "wrap",
            justifyContent: "center",
            maxWidth: "80vw",
          }}
        >
          {ChipList}
          {isEnteringBudget ? (
            <input
              ref={inputRef}
              type="number"
              min="1"
              value={budgetValue}
              onChange={handleBudgetValue}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  onEnterSearch();
                }
              }}
              autoFocus
              style={{
                height: "32px",
                borderRadius: "16px",
                border: "1px solid rgba(0,0,0,0.5)",
                textAlign: "center",
                fontSize: "1em",
                outline: "none",
                padding: "5px",
                width: isXs ? "100px" : "120px",
              }}
              placeholder="Enter Budget"
            />
          ) : (
            <Chip
              label={
                budgetValue
                  ? formatCurrency(budgetValue, "₱", 0)
                  : "Enter Budget"
              }
              size="medium"
              clickable
              sx={{
                backgroundColor: "white",
                border: "1px solid rgba(0,0,0,0.5)",
                fontSize: { xs: ".8em", md: "0.98em" },
                padding: { md: "5px" },
                width: { xs: "100px", md: "120px" },
              }}
              onClick={() => {
                setIsEnteringBudget(true);
                setTimeout(() => inputRef.current?.focus(), 0);
              }}
            />
          )}
        </FlexBox>
      </div>
    </FlexBox>
  );
};

export default AIBanner;
